/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-19
 */

import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import {
  useGetCurrentSubscriptionQuery,
  useGetSubscriptionUsageQuery,
} from '../features/auth/authApi';
import {
  SubscriptionPlan,
  UserSubscription,
  SubscriptionUsage,
} from '../types/subscription';

/**
 * Subscription context type definition
 */
interface SubscriptionContextType {
  // Data
  currentSubscription: UserSubscription | null;
  currentUsage: SubscriptionUsage | null;
  isLoading: boolean;
  error: any;

  // Helper functions for limit checking
  canCreateStudio: () => boolean;
  canAddStudioMember: (studioId?: string) => boolean;
  getRemainingStudios: () => number;
  getRemainingUsers: (studioId?: string) => number;

  // Plan information
  getCurrentPlan: () => SubscriptionPlan | null;
  isFreePlan: () => boolean;
  isTrialActive: () => boolean;
  getTrialDaysRemaining: () => number;
}

/**
 * Create the subscription context
 */
const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

/**
 * Props for the subscription provider
 */
interface SubscriptionProviderProps {
  children: ReactNode;
}

/**
 * Subscription context provider component
 * Provides subscription data and limit checking functions throughout the app
 */
export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
}) => {
  const {
    data: currentSubscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = useGetCurrentSubscriptionQuery();

  const {
    data: currentUsage,
    isLoading: usageLoading,
    error: usageError,
  } = useGetSubscriptionUsageQuery();

  const isLoading = subscriptionLoading || usageLoading;
  const error = subscriptionError || usageError;

  /**
   * Get the current subscription plan
   */
  const getCurrentPlan = useCallback(
    (): SubscriptionPlan | null => currentSubscription?.plan || null,
    [currentSubscription?.plan]
  );

  /**
   * Check if user is on the free plan
   */
  const isFreePlan = useCallback((): boolean => {
    const plan = getCurrentPlan();
    return !plan || plan.name === 'Free' || plan.slug === 'free';
  }, [getCurrentPlan]);

  /**
   * Check if trial is currently active
   */
  const isTrialActive = useCallback((): boolean => {
    if (!currentSubscription?.trial_start || !currentSubscription?.trial_end) {
      return false;
    }

    const now = new Date();
    const trialEnd = new Date(currentSubscription.trial_end);
    return now < trialEnd;
  }, [currentSubscription?.trial_start, currentSubscription?.trial_end]);

  /**
   * Get remaining trial days
   */
  const getTrialDaysRemaining = useCallback((): number => {
    if (!currentSubscription?.trial_end) {
      return 0;
    }

    const now = new Date();
    const trialEnd = new Date(currentSubscription.trial_end);

    // Check if trial is still active
    if (now >= trialEnd) {
      return 0;
    }

    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  }, [currentSubscription?.trial_end]);

  /**
   * Check if user can create a new studio
   */
  const canCreateStudio = useCallback((): boolean => {
    const plan = getCurrentPlan();
    if (!plan || !currentUsage) return false;

    // Unlimited studios (Pro plan typically)
    if (plan.max_owned_studios === 0) return true;

    // Check if under limit
    return (
      (currentUsage.usage?.owned_studios?.count || 0) < plan.max_owned_studios
    );
  }, [getCurrentPlan, currentUsage]);

  /**
   * Check if user can add a member to a studio
   */
  const canAddStudioMember = useCallback(
    (_studioId?: string): boolean => {
      const plan = getCurrentPlan();
      if (!plan) return false;

      // Unlimited users per studio (Pro plan typically)
      if (plan.max_users_per_studio === 0) return true;

      // For now, we don't have per-studio user counts in the usage data
      // This would need to be enhanced with more detailed usage tracking
      // For now, we'll assume users can add members unless we get more specific data
      return true;
    },
    [getCurrentPlan]
  );

  /**
   * Get number of remaining studios user can create
   */
  const getRemainingStudios = useCallback((): number => {
    const plan = getCurrentPlan();
    if (!plan || !currentUsage) return 0;

    // Unlimited studios
    if (plan.max_owned_studios === 0) return Infinity;

    // Calculate remaining
    const remaining =
      plan.max_owned_studios - (currentUsage.usage?.owned_studios?.count || 0);
    return Math.max(0, remaining);
  }, [getCurrentPlan, currentUsage]);

  /**
   * Get number of remaining users for a studio
   */
  const getRemainingUsers = useCallback(
    (_studioId?: string): number => {
      const plan = getCurrentPlan();
      if (!plan) return 0;

      // Unlimited users per studio
      if (plan.max_users_per_studio === 0) return Infinity;

      // For now, return the plan limit since we don't have per-studio user counts
      // This would need to be enhanced with more detailed usage tracking
      return plan.max_users_per_studio;
    },
    [getCurrentPlan]
  );

  const contextValue: SubscriptionContextType = useMemo(
    () => ({
      // Data
      currentSubscription: currentSubscription || null,
      currentUsage: currentUsage || null,
      isLoading,
      error,

      // Helper functions
      canCreateStudio,
      canAddStudioMember,
      getRemainingStudios,
      getRemainingUsers,

      // Plan information
      getCurrentPlan,
      isFreePlan,
      isTrialActive,
      getTrialDaysRemaining,
    }),
    [
      currentSubscription,
      currentUsage,
      isLoading,
      error,
      canCreateStudio,
      canAddStudioMember,
      getRemainingStudios,
      getRemainingUsers,
      getCurrentPlan,
      isFreePlan,
      isTrialActive,
      getTrialDaysRemaining,
    ]
  );

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook to use subscription context
 * Throws error if used outside of SubscriptionProvider
 */
export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);

  if (!context) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider'
    );
  }

  return context;
};

/**
 * Hook to use subscription data with loading states
 * More convenient for components that just need the data
 */
export const useSubscriptionData = () => {
  const { currentSubscription, currentUsage, isLoading, error } =
    useSubscription();

  return {
    subscription: currentSubscription,
    usage: currentUsage,
    isLoading,
    error,
  };
};

/**
 * Hook to use subscription limits
 * More convenient for components that need limit checking
 */
export const useSubscriptionLimits = () => {
  const {
    canCreateStudio,
    canAddStudioMember,
    getRemainingStudios,
    getRemainingUsers,
    getCurrentPlan,
  } = useSubscription();

  return {
    canCreateStudio,
    canAddStudioMember,
    getRemainingStudios,
    getRemainingUsers,
    currentPlan: getCurrentPlan(),
  };
};

export default SubscriptionContext;
